import { ILevelConfig } from '../Config/Levels';
import Tile from '../Tiles/Tile';
import { Suits } from '../Tiles/Suits';
import { IBoardMap, TBoard } from './BoardArrangement';
import { ITileType } from '../Tiles/TileSet';

export type Pair = [number, number];

export interface IBoardData {
    tiles: ITileData[];
    objects?: IBoardObject[];
    config: ILevelConfig;
    // will probably add objective info here and in data too
}

interface IBoardObject {
    position: [number, number, number];
    type: string;
}

export interface ITileData {
    position: [number, number, number];
    type: string;
    // potentially more options from data
}

const BoardUtils = {
    // creates an id from position of tile
    formatBoardId(nums: number[]): string {
        return nums
            .map((n, i, arr) => {
                return i === arr.length - 1 ? n : n + '-';
            })
            .join('');
    },

    locFromId(id: string): number[] {
        // handle negatives
        return id.split('-').reduce((acc, num, index, src) => {
            if (src[index - 1] === '') {
                acc.push(Number(-num));
            } else {
                if (num !== '') {
                    acc.push(Number(num));
                }
            }
            return acc;
        }, []);
    },

    // turns tiletype back into string
    formatId(tileType: ITileType): string {
        return tileType.suit.toString() + '-' + tileType.value.toString();
    },

    // takes string from data and creates tileType
    convertTileData(type: string): ITileType {
        const [t, val] = type.split('-');
        //@ts-ignore
        const suit = Object.values(Suits).includes(t) ? t : 'notset';
        const convertValue = parseInt(val, 10);
        const value = isNaN(convertValue) ? 0 : convertValue;

        return {
            suit,
            value,
        };
    },

    findMatchesFromAvailable(available: string[], boardMap: IBoardMap) {
        const availablePieces: Tile[] = available.reduce((acc, tileId) => {
            if (boardMap[tileId].tileType.suit !== Suits.SPECIAL) {
                acc.push(boardMap[tileId]);
            }
            if (boardMap[tileId].tileType.suit === Suits.BOOSTER) {
                acc.push(boardMap[tileId]);
            }
            return acc;
        }, []);

        return this.findMatches(availablePieces);
    },

    findMatches(arr: TBoard): Tile[] {
        const typesChecked: ITileType[] = [];
        arr.forEach(t => {
            const included = typesChecked.filter(
                t2 => t2.suit === t.tileType.suit && t2.value === t.tileType.value
            );
            if (included.length === 0) {
                typesChecked.push(t.tileType);
            }
        });

        return typesChecked.reduce((acc, t) => {
            let specialMatch = false;
            let numOfType = 0;
            if (t.suit === Suits.FLOWERS || t.suit === Suits.SEASONS) {
                specialMatch = true;
                numOfType = arr.filter(i => i.tileType.suit === t.suit).length;
            } else {
                numOfType = arr.filter(i => this.formatId(i.tileType) === this.formatId(t)).length;
            }

            if (numOfType >= 2) {
                let pairNum: number = Math.floor(numOfType / 2);
                while (pairNum > 0) {
                    const aa = Array(2)
                        .fill('')
                        .map(() => {
                            let p = null;
                            if (specialMatch) {
                                p = arr.find(a => a.tileType.suit === t.suit);
                            } else {
                                p = arr.find(
                                    a => a.tileType.suit === t.suit && a.tileType.value === t.value
                                );
                            }

                            arr.splice(arr.indexOf(p), 1);
                            return p;
                        });
                    acc = [...acc, ...aa];
                    pairNum--;
                }
            }
            return acc;
        }, []);
    },

    createKeys(b: TBoard): string[] {
        return b.reduce((acc, item) => {
            acc.push(this.formatBoardId(item.position));
            // acc.push(item.boardId);
            return acc;
        }, []);
    },

    createBoardMap(b: TBoard): IBoardMap {
        return b.reduce(
            (acc, item) => {
                acc[item.boardId] = item;
                return acc;
            },
            {} as IBoardMap
        );
    },
};

export default BoardUtils;
